import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { isAdminUser, can } from "@/v2/lib/rbac";
import "@/v2/v2.css";
import "@/v2/haramain.css";

// ---------- Official platform (V2) ----------
import V2Login from "@/v2/pages/Login";
import V2Register from "@/v2/pages/Register";
import V2Onboarding from "@/v2/pages/Onboarding";
import V2Pending from "@/v2/pages/Pending";
import V2Layout from "@/v2/V2Layout";
import V2Dashboard from "@/v2/pages/Dashboard";
import V2Journey from "@/v2/pages/Journey";
import V2ChallengeDetail from "@/v2/pages/ChallengeDetail";
import V2Leaderboard from "@/v2/pages/Leaderboard";
import V2Profile from "@/v2/pages/Profile";
import V2Badges from "@/v2/pages/Badges";
import V2Notifications from "@/v2/pages/Notifications";
import V2Pulse from "@/v2/pages/Pulse";

// ---------- Department Manager (مدير إدارة) ----------
import DeptShell from "@/v2/dept/DeptShell";
import DeptOverview from "@/v2/dept/Overview";
import DeptEmployees from "@/v2/dept/Employees";
import DeptLeaderboard from "@/v2/dept/Leaderboard";
import DeptAnalytics from "@/v2/dept/Analytics";
import DeptReports from "@/v2/dept/Reports";
import DeptMotivate from "@/v2/dept/Motivate";
import DeptCompetition from "@/v2/dept/Competition";

// ---------- Admin Control Center ----------
import AdminShell from "@/v2/admin/AdminShell";
import AdminOverview from "@/v2/admin/Overview";
import { PeopleHub, ContentHub, PerformanceHub } from "@/v2/admin/ControlHub";
import AdminAudit from "@/v2/admin/Audit";
import AdminAISettings from "@/v2/admin/Settings";
import AdminPulse from "@/v2/admin/Pulse";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#25343f]">
    <div className="w-12 h-12 rounded-full border-4 border-white/10 border-t-[#bb9661] animate-spin" />
  </div>
);

const homeFor = (user) => (user?.role === "dept_manager" ? "/dept" : isAdminUser(user) ? "/admin" : "/dashboard");

/** بوابة الدخول للمحتوى: الإعداد الأولي ثم الاعتماد ثم المنصة */
const Protected = ({ children, adminOnly }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.needs_onboarding) return <Navigate to="/onboarding" replace />;
  if (user.is_approved === false) return <Navigate to="/pending" replace />;
  if (adminOnly && !isAdminUser(user)) return <Navigate to="/dashboard" replace />;
  return children;
};

const Shell = () => (<Protected><V2Layout /></Protected>);

const Perm = ({ perm, children }) => {
  const { user } = useAuth();
  return can(user, perm) ? children : <Navigate to="/admin" replace />;
};

const LegacyRedirect = () => {
  const { pathname } = useLocation();
  return <Navigate to={pathname.replace(/^\/v2/, "") || "/dashboard"} replace />;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={homeFor(user)} replace /> : <V2Login />} />
      <Route path="/register" element={user ? <Navigate to={homeFor(user)} replace /> : <V2Register />} />
      <Route path="/onboarding" element={
        !user ? <Navigate to="/login" replace />
          : user.needs_onboarding ? <V2Onboarding />
            : user.is_approved ? <Navigate to={homeFor(user)} replace /> : <Navigate to="/pending" replace />} />
      <Route path="/pending" element={
        !user ? <Navigate to="/login" replace />
          : user.needs_onboarding ? <Navigate to="/onboarding" replace />
            : user.is_approved ? <Navigate to={homeFor(user)} replace /> : <V2Pending />} />

      {/* الدخول المباشر إلى المنصة مع الاحتفاظ ببوابات الحساب الحالية */}
      <Route path="/" element={<Navigate to={user ? homeFor(user) : "/login"} replace />} />

      <Route element={<Shell />}>
        <Route path="/dashboard" element={<V2Dashboard />} />
        <Route path="/journey" element={<V2Journey />} />
        <Route path="/journey/:id" element={<V2ChallengeDetail />} />
        <Route path="/leaderboard" element={<V2Leaderboard />} />
        <Route path="/badges" element={<V2Badges />} />
        <Route path="/notifications" element={<V2Notifications />} />
        <Route path="/profile" element={<V2Profile />} />
        <Route path="/pulse" element={<V2Pulse />} />

        <Route path="/dept" element={<DeptShell />}>
          <Route index element={<DeptOverview />} />
          <Route path="employees" element={<DeptEmployees />} />
          <Route path="leaderboard" element={<DeptLeaderboard />} />
          <Route path="competition" element={<DeptCompetition />} />
          <Route path="analytics" element={<DeptAnalytics />} />
          <Route path="reports" element={<DeptReports />} />
          <Route path="motivate" element={<DeptMotivate />} />
        </Route>

        <Route path="/admin" element={<Protected adminOnly><AdminShell /></Protected>}>
          <Route index element={<AdminOverview />} />
          <Route path="people" element={<Perm perm="manage_users"><PeopleHub /></Perm>} />
          <Route path="people/:section" element={<Perm perm="manage_users"><PeopleHub /></Perm>} />
          <Route path="content" element={<Perm perm="manage_content"><ContentHub /></Perm>} />
          <Route path="content/:section" element={<Perm perm="manage_content"><ContentHub /></Perm>} />
          <Route path="performance" element={<PerformanceHub />} />
          <Route path="performance/:section" element={<PerformanceHub />} />
          <Route path="pulse" element={<Perm perm="manage_pulse"><AdminPulse /></Perm>} />
          <Route path="audit" element={<Perm perm="view_audit"><AdminAudit /></Perm>} />
          <Route path="settings" element={<Perm perm="manage_settings"><AdminAISettings /></Perm>} />

          {/* الروابط القديمة تبقى صالحة وتنتقل إلى مركزها الجديد */}
          <Route path="join-requests" element={<Navigate to="/admin/people/requests" replace />} />
          <Route path="employees" element={<Navigate to="/admin/people/employees" replace />} />
          <Route path="departments" element={<Navigate to="/admin/people/departments" replace />} />
          <Route path="competencies" element={<Navigate to="/admin/content/competencies" replace />} />
          <Route path="paths" element={<Navigate to="/admin/content/paths" replace />} />
          <Route path="challenges" element={<Navigate to="/admin/content/challenges" replace />} />
          <Route path="videos" element={<Navigate to="/admin/content/videos" replace />} />
          <Route path="files" element={<Navigate to="/admin/content/files" replace />} />
          <Route path="quizzes" element={<Navigate to="/admin/content/quizzes" replace />} />
          <Route path="analytics" element={<Navigate to="/admin/performance/analytics" replace />} />
          <Route path="results" element={<Navigate to="/admin/performance/results" replace />} />
          <Route path="points" element={<Navigate to="/admin/performance/points" replace />} />
          <Route path="reports" element={<Navigate to="/admin/performance/reports" replace />} />
        </Route>
      </Route>

      {/* old V2 links keep working */}
      <Route path="/v2/*" element={<LegacyRedirect />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.dir = "rtl";
    document.documentElement.lang = "ar";
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors dir="rtl" />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
